import { useState } from "react";
import { Search, SlidersHorizontal, X, ChevronDown, ChevronRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { componentCategories } from "@/data/componentCategories";
import { cn } from "@/lib/utils";

const finishes = ["All", "Polished", "Matte", "Glossy", "Textured", "Brushed", "Natural", "Anodized"];
const sortOptions = [
  { value: "newest", label: "Newest" },
  { value: "name", label: "Name A-Z" },
];

interface ComponentFiltersProps {
  onSearch: (query: string) => void;
  onMainCategoryChange: (category: string) => void;
  onSubCategoryChange: (subCategory: string) => void;
  onFinishChange: (finish: string) => void;
  onSortChange: (sort: string) => void;
  activeFilters: {
    search: string;
    mainCategory: string;
    subCategory: string;
    finish: string;
    sort: string;
  };
}

export function ComponentFilters({
  onSearch,
  onMainCategoryChange,
  onSubCategoryChange,
  onFinishChange,
  onSortChange,
  activeFilters,
}: ComponentFiltersProps) {
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<string[]>([activeFilters.mainCategory]);

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories((prev) =>
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handleMainCategoryClick = (categoryId: string) => {
    if (activeFilters.mainCategory === categoryId) {
      onMainCategoryChange("All");
      onSubCategoryChange("All");
    } else {
      onMainCategoryChange(categoryId);
      onSubCategoryChange("All");
      if (!expandedCategories.includes(categoryId)) {
        toggleCategory(categoryId);
      }
    }
  };

  const handleSubCategoryClick = (mainCategoryId: string, subCategoryId: string) => {
    if (activeFilters.subCategory === subCategoryId) {
      onSubCategoryChange("All");
    } else {
      onMainCategoryChange(mainCategoryId);
      onSubCategoryChange(subCategoryId);
    }
  };

  const activeFilterCount = [
    activeFilters.mainCategory !== "All",
    activeFilters.subCategory !== "All",
    activeFilters.finish !== "All",
  ].filter(Boolean).length;

  const CategoryTree = () => (
    <div className="space-y-1">
      {componentCategories.map((mainCat) => (
        <Collapsible
          key={mainCat.id}
          open={expandedCategories.includes(mainCat.id)}
          onOpenChange={() => toggleCategory(mainCat.id)}
        >
          <div className="flex items-center">
            <CollapsibleTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
              >
                {expandedCategories.includes(mainCat.id) ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </Button>
            </CollapsibleTrigger>
            <button
              onClick={() => handleMainCategoryClick(mainCat.id)}
              className={cn(
                "flex-1 rounded-md px-2 py-1.5 text-left text-sm font-medium transition-colors",
                activeFilters.mainCategory === mainCat.id
                  ? "bg-primary text-primary-foreground"
                  : "text-foreground hover:bg-secondary"
              )}
            >
              {mainCat.name}
            </button>
          </div>
          <CollapsibleContent className="pl-8 pt-1">
            <div className="space-y-0.5 border-l border-border pl-2">
              {mainCat.subCategories.map((subCat) => (
                <button
                  key={subCat.id}
                  onClick={() => handleSubCategoryClick(mainCat.id, subCat.id)}
                  className={cn(
                    "block w-full rounded-md px-2 py-1.5 text-left text-sm transition-colors",
                    activeFilters.subCategory === subCat.id
                      ? "bg-primary/10 text-primary font-medium"
                      : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                  )}
                >
                  {subCat.name}
                </button>
              ))}
            </div>
          </CollapsibleContent>
        </Collapsible>
      ))}
    </div>
  );

  const FilterContent = () => (
    <div className="space-y-6">
      {/* Category Tree */}
      <div>
        <h4 className="mb-3 text-sm font-medium text-foreground">Categories</h4>
        <CategoryTree />
      </div>

      {/* Finish */}
      <div>
        <h4 className="mb-3 text-sm font-medium text-foreground">Finish</h4>
        <div className="flex flex-wrap gap-2">
          {finishes.map((finish) => (
            <Badge
              key={finish}
              variant={activeFilters.finish === finish ? "default" : "outline"}
              className="cursor-pointer transition-colors hover:bg-primary hover:text-primary-foreground"
              onClick={() => onFinishChange(finish)}
            >
              {finish}
            </Badge>
          ))}
        </div>
      </div>

    </div>
  );

  // Get readable names for active filters
  const getActiveMainCategoryName = () => {
    return componentCategories.find((c) => c.id === activeFilters.mainCategory)?.name;
  };

  const getActiveSubCategoryName = () => {
    for (const cat of componentCategories) {
      const sub = cat.subCategories.find((s) => s.id === activeFilters.subCategory);
      if (sub) return sub.name;
    }
    return null;
  };

  return (
    <div className="space-y-4">
      {/* Search and Sort Row */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search components..."
            value={activeFilters.search}
            onChange={(e) => onSearch(e.target.value)}
            className="pl-10 bg-card"
          />
        </div>

        <div className="flex items-center gap-3">
          {/* Mobile Filter Toggle */}
          <Sheet open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="outline" className="gap-2">
                <SlidersHorizontal className="h-4 w-4" />
                Filters
                {activeFilterCount > 0 && (
                  <Badge variant="secondary" className="ml-1 h-5 w-5 rounded-full p-0 text-xs">
                    {activeFilterCount}
                  </Badge>
                )}
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-80 bg-background overflow-y-auto">
              <SheetHeader>
                <SheetTitle className="font-serif">Filters</SheetTitle>
              </SheetHeader>
              <div className="mt-6">
                <FilterContent />
              </div>
            </SheetContent>
          </Sheet>

          <Select value={activeFilters.sort} onValueChange={onSortChange}>
            <SelectTrigger className="w-[180px] bg-card">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent className="bg-popover">
              {sortOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Desktop Filters */}
      <div className="hidden rounded-xl border border-border bg-card p-6 lg:block">
        <FilterContent />
      </div>

      {/* Active Filters Display */}
      {activeFilterCount > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted-foreground">Active filters:</span>
          {activeFilters.mainCategory !== "All" && (
            <Badge variant="secondary" className="gap-1">
              {getActiveMainCategoryName()}
              <X
                className="h-3 w-3 cursor-pointer"
                onClick={() => {
                  onMainCategoryChange("All");
                  onSubCategoryChange("All");
                }}
              />
            </Badge>
          )}
          {activeFilters.subCategory !== "All" && (
            <Badge variant="secondary" className="gap-1">
              {getActiveSubCategoryName()}
              <X
                className="h-3 w-3 cursor-pointer"
                onClick={() => onSubCategoryChange("All")}
              />
            </Badge>
          )}
          {activeFilters.finish !== "All" && (
            <Badge variant="secondary" className="gap-1">
              {activeFilters.finish}
              <X
                className="h-3 w-3 cursor-pointer"
                onClick={() => onFinishChange("All")}
              />
            </Badge>
          )}
        </div>
      )}
    </div>
  );
}
